<template>
  <dashboard-layout title="Celebrity & Historical Twins" subtitle="Queue, generation cost & report content">
    <template #actions>
      <router-link to="/twin-figures" class="link-btn">Figure pool →</router-link>
      <search-box v-model="search" placeholder="Search user / report id / figure…" />
      <button class="refresh-btn" @click="load" :disabled="loading">{{ loading ? 'Loading…' : 'Refresh' }}</button>
    </template>

    <div v-if="error" class="error-banner">⚠ {{ error }}</div>

    <!-- Live: runs generating right now, filling in as sections land (onSnapshot) -->
    <data-panel>
      <template #title>
        Live <span class="muted">({{ inFlight.length }})</span>
        <span class="live-dot" :class="{ on: watching }" :title="watching ? 'Watching live' : 'Connecting…'"></span>
      </template>
      <template #actions>
        <span v-if="staleCount" class="stale-flag">⚠ {{ staleCount }} stale</span>
      </template>
      <div v-if="!inFlight.length" class="live-idle">No runs generating right now — new runs appear here live as they start.</div>
      <div v-else class="live-cards">
        <div v-for="r in inFlight" :key="r.id" class="live-card" :class="{ stale: isStale(r) }">
          <div class="lc-top">
            <status-badge :variant="reportStateVariant(r)" :label="reportStateLabel(r)" />
            <span class="lc-title" :title="r.title">{{ r.user.name || r.title || 'Untitled' }}</span>
            <span class="lc-elapsed">{{ reportDuration(r) }}</span>
          </div>
          <div class="lc-meta">
            <a v-if="r.userId" :href="`/user/${r.userId}`" class="user-link" @click.prevent="openUser(r.userId, $event)">{{ truncate(r.userId, 12) }}</a>
            <span v-else class="muted">—</span>
            <span v-if="r.language"> · {{ r.language }}</span>
            <span v-if="r.matches.length"> · {{ r.matches.map(m => m.firstName || m.name).filter(Boolean).join(', ') }}</span>
          </div>
          <div class="lc-stages">
            <div v-for="sec in SLICES" :key="sec" class="stage" :class="'st-' + stageState(r, sec)">
              <span class="stage-ic">{{ stageState(r, sec) === 'ready' ? '✓' : (stageState(r, sec) === 'failed' ? '✕' : '') }}</span>
              {{ sliceLabel(sec) }}
            </div>
          </div>
          <div class="lc-prog"><div class="lc-prog-fill" :style="{ width: (readySections(r) / SLICES.length * 100) + '%' }"></div></div>
          <div class="lc-count">{{ readySections(r) }}/{{ SLICES.length }} sections ready</div>
        </div>
      </div>
    </data-panel>

    <loading-state v-if="loading && !generations.length && !stats && !reports.length" label="Loading Celebrity Twins cost…" />

    <template v-else>
      <!-- Overview strip: reportStats rollup + live in-flight count -->
      <stat-grid>
        <stat-card label="Reports" :value="(stats ? stats.count : 0).toLocaleString('en-US')" icon="filetext" accent>
          <template #sub>{{ stats ? stats.completed : 0 }} complete · {{ stats ? stats.partial : 0 }} partial · {{ failedCount }} failed</template>
        </stat-card>
        <stat-card label="In progress" :value="runningCount.toLocaleString('en-US')" icon="activity">
          <template #sub v-if="staleCount">{{ staleCount }} stale (>4 min)</template>
          <template #sub v-else>generating right now</template>
        </stat-card>
        <stat-card label="Avg cost / report" :value="formatCost(avgCost)" icon="dollar">
          <template #sub>full only {{ formatCost(avgCostFull) }} · excl. repairs</template>
        </stat-card>
        <stat-card label="Total spent" :value="formatCost(stats ? stats.totalCostUSD : 0)" icon="creditcard">
          <template #sub>{{ (stats ? stats.count : 0).toLocaleString('en-US') }} generations · {{ stats ? stats.repairs : 0 }} repairs</template>
        </stat-card>
        <stat-card label="Avg duration" :value="formatMs(avgMs)" icon="sliders">
          <template #sub v-if="stats && stats.lastAtMs">last {{ formatDate(stats.lastAtMs) }}</template>
        </stat-card>
      </stat-grid>

      <p class="note">
        Headline numbers are the all-time <code>reportStats/celebrityTwins</code> rollup. The chart and slice
        breakdown reflect the {{ generations.length.toLocaleString('en-US') }} loaded
        generation{{ generations.length === 1 ? '' : 's' }}. Synchronous (no queue, no PDF); the three figure
        matches run in parallel (~$0.04–0.06 each), then the synthesis closes after them (~$0.01).
      </p>

      <!-- Cost per day -->
      <data-panel v-if="generations.length" title="Cost per day (USD)">
        <div class="chart-box"><canvas ref="dayCanvas"></canvas></div>
      </data-panel>

      <!-- Cost by slice -->
      <div v-if="generations.length" class="dual-grid">
        <data-panel title="Cost by slice">
          <div class="chart-box small"><canvas ref="sliceCanvas"></canvas></div>
        </data-panel>
        <data-panel title="Avg cost & duration per slice (loaded)">
          <div class="slice-bars">
            <div v-for="s in sliceAgg" :key="s.key" class="slice-bar-row">
              <span class="sb-label">{{ sliceLabel(s.key) }}</span>
              <div class="sb-track"><div class="sb-fill" :style="{ width: s.pct + '%', background: sliceColor(s.key) }"></div></div>
              <span class="sb-val">{{ formatCost(s.avg) }}</span>
              <span class="sb-val2">{{ formatMs(s.avgMs) }}</span>
            </div>
          </div>
        </data-panel>
      </div>

      <!-- Generations (cost rows) -->
      <div class="filters">
        <filter-chips v-model="stateFilter" :options="stateOptions" />
      </div>
      <data-panel flush>
        <template #title>Generations <span class="muted">({{ filteredGenerations.length.toLocaleString('en-US') }})</span></template>
        <data-table
          :columns="genColumns"
          :rows="filteredGenerations"
          row-key="id"
          clickable
          :expanded-key="expandedGen"
          @row-click="(row) => (expandedGen = expandedGen === row.id ? null : row.id)"
        >
          <template #cell-created="{ row }">
            <span class="date-cell"><span class="caret" :class="{ open: expandedGen === row.id }">▸</span> {{ formatDate(row.createdAtMs) }}</span>
          </template>
          <template #cell-user="{ row }">
            <a v-if="row.userId" :href="`/user/${row.userId}`" class="user-link" @click.stop.prevent="openUser(row.userId, $event)">{{ truncate(row.userId, 10) }}</a>
            <span v-else class="muted">—</span>
          </template>
          <template #cell-language="{ row }">{{ row.language || '—' }}</template>
          <template #cell-state="{ row }">
            <status-badge :variant="genStateVariant(row.state)" :label="row.state || '—'" />
            <status-badge v-if="row.partialRepair" variant="info" label="repair" class="repair-tag" />
          </template>
          <template #cell-cost="{ row }"><span class="strong">{{ formatCost(row.costUsd) }}</span></template>
          <template #cell-duration="{ row }">{{ formatMs(row.totalMs) }}</template>
          <template #cell-tokens="{ row }">{{ formatTokens(row.inputTokens) }} / {{ formatTokens(row.outputTokens) }}</template>
          <template #cell-failed="{ row }">
            <span v-if="row.slicesFailed.length" class="failed">{{ row.slicesFailed.join(', ') }}</span>
            <span v-else class="muted">—</span>
          </template>
          <template #empty>No generations match your filters</template>

          <template #expanded="{ row }">
            <div class="slice-detail">
              <table class="slice-table">
                <thead>
                  <tr><th>Slice</th><th>Model</th><th>OK</th><th>Cost</th><th>Duration</th><th>In/Out</th><th>Think</th><th>Att.</th></tr>
                </thead>
                <tbody>
                  <tr v-for="key in SLICES" :key="key" :class="{ 'slice-missing': !row.slices[key] }">
                    <td class="sl-name">{{ sliceLabel(key) }}</td>
                    <td class="sl-model">{{ modelShort(sliceOf(row, key).model) }}</td>
                    <td>
                      <span v-if="row.slices[key]" :class="sliceOf(row, key).ok ? 'ok-yes' : 'ok-no'">{{ sliceOf(row, key).ok ? '✓' : '✕' }}</span>
                      <span v-else class="muted">—</span>
                    </td>
                    <td>{{ row.slices[key] ? formatCost(num(sliceOf(row, key).costUsd)) : '—' }}</td>
                    <td>{{ row.slices[key] ? formatMs(num(sliceOf(row, key).ms)) : '—' }}</td>
                    <td>{{ row.slices[key] ? (formatTokens(num(sliceOf(row, key).inputTokens)) + ' / ' + formatTokens(num(sliceOf(row, key).outputTokens))) : '—' }}</td>
                    <td>{{ row.slices[key] && sliceOf(row, key).thinkingTokens ? formatTokens(num(sliceOf(row, key).thinkingTokens)) : '—' }}</td>
                    <td>{{ row.slices[key] ? num(sliceOf(row, key).attempts) : '—' }}</td>
                  </tr>
                </tbody>
              </table>
              <div class="slice-foot">
                <span>Report: <code>{{ row.reportId || '—' }}</code></span>
                <span v-if="row.promptVersion">Prompt: <code>{{ row.promptVersion }}</code></span>
                <span v-if="row.poolSize">Pool: <code>{{ row.poolSize }}</code></span>
                <span v-if="row.matches.length">Picked: <code>{{ row.matches.map(m => m.id).join(', ') }}</code></span>
              </div>
            </div>
          </template>
        </data-table>
      </data-panel>

      <!-- Recent reports (queue + content detail) -->
      <data-panel flush>
        <template #title>Recent reports <span class="muted">({{ filteredReports.length.toLocaleString('en-US') }})</span></template>
        <template #actions>
          <span v-if="runningCount" class="running-flag">● {{ runningCount }} running</span>
          <span v-if="staleCount" class="stale-flag">⚠ {{ staleCount }} stale</span>
        </template>
        <data-table
          :columns="reportColumns"
          :rows="filteredReports"
          row-key="id"
          clickable
          :expanded-key="expandedReport"
          @row-click="(row) => (expandedReport = expandedReport === row.id ? null : row.id)"
        >
          <template #cell-created="{ row }">
            <span class="date-cell"><span class="caret" :class="{ open: expandedReport === row.id }">▸</span> {{ formatDate(row.createdAtMs) }}</span>
          </template>
          <template #cell-state="{ row }">
            <status-badge :variant="reportStateVariant(row)" :label="reportStateLabel(row)" />
            <status-badge v-if="needsRepair(row)" variant="warning" label="repair" class="repair-tag" />
          </template>
          <template #cell-user="{ row }">
            <div class="ellip" :title="row.user.name">{{ row.user.name || '—' }}</div>
            <a v-if="row.userId" :href="`/user/${row.userId}`" class="user-link small" @click.stop.prevent="openUser(row.userId, $event)">{{ truncate(row.userId, 10) }}</a>
          </template>
          <template #cell-matches="{ row }">
            <div v-if="row.matches.length" class="match-mini">
              <span v-for="m in row.matches" :key="m.slot" class="mm-row">
                <span class="mm-name" :title="m.name">{{ truncate(m.name, 16) }}</span>
                <span class="mm-score">{{ m.score }}</span>
                <status-badge :variant="tierVariant(m.tier)" :label="m.tier" />
              </span>
            </div>
            <span v-else class="muted">—</span>
          </template>
          <template #cell-sections="{ row }">
            <span class="dots">
              <span v-for="sec in SLICES" :key="sec" class="dot" :class="'d-' + sectionState(row, sec)" :title="sliceLabel(sec) + ': ' + (row.sections[sec] || 'pending')"></span>
            </span>
          </template>
          <template #cell-pool="{ row }">{{ row.poolSize || '—' }}</template>
          <template #cell-duration="{ row }">{{ reportDuration(row) }}</template>
          <template #cell-cost="{ row }">
            <span v-if="genForReport(row.reportId)" class="strong">{{ formatCost(genForReport(row.reportId).costUsd) }}</span>
            <span v-else class="muted">—</span>
          </template>
          <template #empty>No celebrity twins reports yet.</template>

          <template #expanded="{ row }">
            <div class="rep-detail">
              <!-- reader -->
              <div class="rd-section rd-reader">
                <div class="rd-title">{{ row.user.name || 'Reader' }} <span class="muted">· {{ row.language || '—' }} · {{ row.user.timeAccuracy || '—' }} time</span></div>
                <div class="reader-three">
                  <span class="three-pill"><span>Sun</span><b>{{ row.user.sun || '—' }}</b></span>
                  <span class="three-pill"><span>Moon</span><b>{{ row.user.moon || '—' }}</b></span>
                  <span class="three-pill"><span>Rising</span><b>{{ row.user.rising || '—' }}</b></span>
                  <span v-if="row.user.dominant" class="three-pill"><span>Dominant</span><b>{{ row.user.dominant }}</b></span>
                  <span v-if="row.user.birthDate" class="three-pill"><span>Born</span><b>{{ row.user.birthDate }}</b></span>
                </div>
              </div>

              <!-- three match cards -->
              <div class="match-cards">
                <div v-for="card in matchCards(row)" :key="card.slot" class="match-card">
                  <div class="mc-head">
                    <div class="mc-photo">
                      <img v-if="card.photo && card.photo.url" :src="card.photo.url" :alt="card.name" loading="lazy" referrerpolicy="no-referrer" @error="onImgError">
                      <div v-else class="mc-nophoto">{{ (card.firstName || card.name || '?').slice(0, 1) }}</div>
                    </div>
                    <div class="mc-id">
                      <div class="mc-name">{{ card.name || '—' }}</div>
                      <div class="mc-sub">
                        <span class="era-tag">{{ card.era || '—' }}</span>
                        <span v-if="card.category"> · {{ card.category }}</span>
                      </div>
                      <div class="mc-sub" v-if="card.born">
                        {{ card.born }}<span v-if="card.yearsApart"> · {{ card.yearsApart }}y {{ card.bornBefore ? 'before' : 'after' }}</span>
                      </div>
                      <div class="mc-three">{{ [card.sun && ('☉ ' + card.sun), card.moon && ('☾ ' + card.moon), card.rising && ('↑ ' + card.rising)].filter(Boolean).join('  ') }}</div>
                    </div>
                    <div class="mc-score">
                      <div class="mc-score-num">{{ card.score || '—' }}</div>
                      <status-badge :variant="tierVariant(card.tier)" :label="card.tier" />
                    </div>
                  </div>

                  <div v-if="card.drivers.length" class="mc-drivers">
                    <span v-for="(d, i) in card.drivers" :key="i" class="driver-chip">{{ d }}</span>
                  </div>

                  <template v-if="card.prose">
                    <div class="mc-profession" v-if="card.prose.profession">{{ card.prose.profession }}</div>
                    <div v-if="card.prose.traits && card.prose.traits.length" class="mc-traits">
                      <span v-for="(t, i) in card.prose.traits" :key="i" class="trait-chip">{{ t }}</span>
                    </div>
                    <p class="mc-summary" v-if="card.prose.summary">{{ card.prose.summary }}</p>

                    <div v-if="card.prose.patterns && card.prose.patterns.length" class="patterns">
                      <div
                        v-for="(p, pi) in card.prose.patterns"
                        :key="pi"
                        class="pattern"
                        :class="{ open: isPatternOpen(row.id, card.slot, pi) }"
                        @click="togglePattern(row.id, card.slot, pi)"
                      >
                        <div class="pat-head">
                          <span class="caret" :class="{ open: isPatternOpen(row.id, card.slot, pi) }">▸</span>
                          <span class="pat-title">{{ p.title }}</span>
                          <span class="pat-hook">{{ p.hook }}</span>
                        </div>
                        <div v-if="isPatternOpen(row.id, card.slot, pi)" class="pat-body">
                          <p v-if="p.you"><span class="pat-who">You</span> {{ p.you }}</p>
                          <p v-if="p.them"><span class="pat-who">{{ card.firstName || 'Them' }}</span> {{ p.them }}</p>
                          <div v-if="p.sharedPattern" class="pat-shared">{{ p.sharedPattern }}</div>
                        </div>
                      </div>
                    </div>

                    <div v-if="card.prose.houses" class="mc-houses">
                      <div class="mc-mini-title">{{ card.prose.houses.headline }}</div>
                      <p class="mc-note">{{ card.prose.houses.note }}</p>
                    </div>

                    <div v-if="card.prose.paths" class="paths">
                      <div class="mc-mini-title">{{ card.prose.paths.headline }}</div>
                      <table class="paths-table">
                        <thead><tr><th></th><th v-for="(c, ci) in card.prose.paths.columns" :key="ci">{{ c }}</th></tr></thead>
                        <tbody>
                          <tr><td class="pt-who">You</td><td v-for="(c, ci) in card.prose.paths.you" :key="ci">{{ c }}</td></tr>
                          <tr><td class="pt-who">{{ card.firstName || 'Them' }}</td><td v-for="(c, ci) in card.prose.paths.them" :key="ci">{{ c }}</td></tr>
                        </tbody>
                      </table>
                      <p class="mc-note" v-if="card.prose.paths.note">{{ card.prose.paths.note }}</p>
                    </div>

                    <div v-if="card.prose.learn" class="learn">
                      <div class="mc-mini-title">{{ card.prose.learn.headline }}</div>
                      <p class="mc-note">{{ card.prose.learn.body }}</p>
                      <ul v-if="card.prose.learn.tryThis && card.prose.learn.tryThis.length" class="try-list">
                        <li v-for="(t, i) in card.prose.learn.tryThis" :key="i">{{ t }}</li>
                      </ul>
                    </div>

                    <div v-if="card.prose.shareTrait" class="share-trait">Share card: <b>{{ card.prose.shareTrait }}</b></div>
                  </template>
                  <div v-else class="mc-pending">
                    Section <b>{{ sectionState(row, 'match' + card.slot) }}</b> — prose not written yet.
                  </div>
                </div>
              </div>

              <!-- synthesis -->
              <div class="rd-section synthesis" v-if="row.content.synthesis">
                <div class="rd-title">What your three matches reveal</div>
                <div class="syn-headline">{{ row.content.synthesis.headline }}</div>
                <p class="mc-note">{{ row.content.synthesis.body }}</p>
              </div>

              <!-- per-slice timing/cost from the generation row -->
              <div class="rd-section" v-if="genForReport(row.reportId)">
                <div class="rd-title">Generation <span class="muted">· {{ formatCost(genForReport(row.reportId).costUsd) }} · {{ formatMs(genForReport(row.reportId).totalMs) }}<template v-if="genForReport(row.reportId).partialRepair"> · repair run</template></span></div>
                <div class="parts-grid">
                  <span v-for="sec in SLICES" :key="sec" class="part-chip" :class="'ps-' + sectionState(row, sec)">
                    {{ sliceLabel(sec) }}
                    <template v-if="sliceOf(genForReport(row.reportId), sec).costUsd"> · {{ formatCost(num(sliceOf(genForReport(row.reportId), sec).costUsd)) }}</template>
                    <template v-if="sliceOf(genForReport(row.reportId), sec).ms"> · {{ formatMs(num(sliceOf(genForReport(row.reportId), sec).ms)) }}</template>
                    <span v-if="row.sectionsReadyAtMs[sec]" class="part-at">{{ formatClock(row.sectionsReadyAtMs[sec]) }}</span>
                  </span>
                </div>
              </div>
              <div class="rd-section" v-else>
                <div class="rd-title">Run</div>
                <div class="parts-grid">
                  <span v-for="sec in SLICES" :key="sec" class="part-chip" :class="'ps-' + sectionState(row, sec)">
                    {{ sliceLabel(sec) }}<span v-if="row.sectionsReadyAtMs[sec]" class="part-at">{{ formatClock(row.sectionsReadyAtMs[sec]) }}</span>
                  </span>
                </div>
                <div class="rd-run-meta">
                  <span>Report <code>{{ row.reportId }}</code></span>
                  <span v-if="row.promptVersion">Prompt <code>{{ row.promptVersion }}</code></span>
                  <span v-if="row.poolSize">Pool <code>{{ row.poolSize }}</code></span>
                </div>
              </div>
            </div>
          </template>
        </data-table>
      </data-panel>

      <!-- Errors -->
      <data-panel flush>
        <template #title>Errors <span class="muted">({{ errorLogs.length }})</span> <span class="muted small">· last 7 days</span></template>
        <data-table :columns="errorColumns" :rows="errorLogs" row-key="id">
          <template #cell-time="{ row }"><span class="nowrap">{{ formatDate(row.createdAtMs) }}</span></template>
          <template #cell-level="{ row }"><status-badge :variant="row.level === 'error' ? 'danger' : 'warning'" :label="row.level" /></template>
          <template #cell-message="{ row }"><span class="ellip wide" :title="row.message">{{ row.message || '—' }}</span></template>
          <template #cell-error="{ row }"><span class="ellip" :title="row.error">{{ row.error || '—' }}</span></template>
          <template #cell-failed="{ row }">
            <span v-if="row.failedSlices" class="failed">{{ Array.isArray(row.failedSlices) ? row.failedSlices.join(', ') : row.failedSlices }}</span>
            <span v-else class="muted">—</span>
          </template>
          <template #cell-reportId="{ row }"><span class="mono">{{ truncate(row.reportId, 14) || '—' }}</span></template>
          <template #empty>No errors in the last 7 days for generateCelebrityTwinsReport. 🎉</template>
        </data-table>
      </data-panel>
    </template>
  </dashboard-layout>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRouter } from 'vue-router'
import { Chart } from 'chart.js/auto'
import { FirebaseCelebrityTwinsReportService, TWINS_SLICES } from '../services/firebaseCelebrityTwinsReportService'
import DashboardLayout from './ui/DashboardLayout.vue'
import StatGrid from './ui/StatGrid.vue'
import StatCard from './ui/StatCard.vue'
import DataPanel from './ui/DataPanel.vue'
import DataTable from './ui/DataTable.vue'
import SearchBox from './ui/SearchBox.vue'
import FilterChips from './ui/FilterChips.vue'
import LoadingState from './ui/LoadingState.vue'
import StatusBadge from './ui/StatusBadge.vue'

const SLICE_COLORS = { match1: '#6c63ff', match2: '#22d3ee', match3: '#f8bf71', synthesis: '#4ade80' }
const SLICE_LABELS = { match1: 'Match 1', match2: 'Match 2', match3: 'Match 3', synthesis: 'Synthesis' }
const STALE_MS = 4 * 60 * 1000 // backend treats a run older than 4 min as dead

const genColumns = [
  { key: 'created', label: 'Created', width: '160px' },
  { key: 'user', label: 'User', width: '100px' },
  { key: 'language', label: 'Lang', width: '60px' },
  { key: 'state', label: 'State', width: '130px' },
  { key: 'cost', label: 'Cost', width: '90px' },
  { key: 'duration', label: 'Duration', width: '90px' },
  { key: 'tokens', label: 'Tok in/out', width: '120px' },
  { key: 'failed', label: 'Failed slices' }
]
const reportColumns = [
  { key: 'created', label: 'Created', width: '160px' },
  { key: 'state', label: 'State', width: '120px' },
  { key: 'user', label: 'User', width: '130px' },
  { key: 'matches', label: 'Matches' },
  { key: 'sections', label: 'Sections', width: '80px' },
  { key: 'pool', label: 'Pool', width: '55px' },
  { key: 'duration', label: 'Duration', width: '90px' },
  { key: 'cost', label: 'Cost', width: '90px' }
]
const errorColumns = [
  { key: 'time', label: 'Time', width: '150px' },
  { key: 'level', label: 'Level', width: '80px' },
  { key: 'message', label: 'Message' },
  { key: 'error', label: 'Error', width: '220px' },
  { key: 'failed', label: 'Failed', width: '120px' },
  { key: 'reportId', label: 'Report', width: '130px' }
]

function num(v) {
  if (typeof v === 'number') return v
  if (v == null) return 0
  const n = Number(v)
  return isNaN(n) ? 0 : n
}

export default {
  name: 'CelebrityTwinsReportsDashboard',
  components: { DashboardLayout, StatGrid, StatCard, DataPanel, DataTable, SearchBox, FilterChips, LoadingState, StatusBadge },
  setup() {
    const router = useRouter()
    const service = new FirebaseCelebrityTwinsReportService()

    const stats = ref(null)
    const generations = ref([])
    const reports = ref([])
    const errorLogs = ref([])
    const loading = ref(true)
    const error = ref('')
    const search = ref('')
    const stateFilter = ref('all')
    const expandedGen = ref(null)
    const expandedReport = ref(null)
    const openPatterns = ref({})
    const now = ref(Date.now())
    const watching = ref(false)

    const dayCanvas = ref(null)
    const sliceCanvas = ref(null)
    let dayChart = null
    let sliceChart = null
    let ticker = null
    let unsub = null

    // Stats, generations and errors are one-shot (Refresh re-pulls them). Reports
    // are watched live (startWatch) so in-flight runs fill in while Hakan watches.
    const load = async () => {
      loading.value = true
      error.value = ''
      try {
        const [s, g, e] = await Promise.all([service.getStats(), service.getGenerations(), service.getErrorLogs()])
        stats.value = s
        generations.value = g
        errorLogs.value = e
      } catch (e) {
        console.error(e)
        const code = e && e.code ? e.code : ''
        if (code === 'permission-denied') error.value = 'Firestore denied the read (not signed in). Reload and try again.'
        else if (code === 'failed-precondition') error.value = (e && e.message) || 'A Firestore index is required for this query.'
        else error.value = (e && e.message) || 'Failed to load Celebrity Twins cost.'
      } finally {
        loading.value = false
      }
    }

    const startWatch = () => {
      unsub = service.watchReports(
        rows => { reports.value = rows; watching.value = true },
        err => { watching.value = false; error.value = (err && err.message) || 'Live report watch failed.' }
      )
    }

    const avgCost = computed(() => (stats.value && stats.value.count) ? stats.value.totalCostUSD / stats.value.count : 0)
    const avgCostFull = computed(() => {
      if (!stats.value) return 0
      const full = stats.value.count - stats.value.repairs
      return full > 0 ? stats.value.totalCostUSD / full : 0
    })
    const avgMs = computed(() => (stats.value && stats.value.count) ? stats.value.totalMs / stats.value.count : 0)
    // reportStats keeps no failed counter; derive it from the rollup.
    const failedCount = computed(() => {
      if (!stats.value) return 0
      return Math.max(0, stats.value.count - stats.value.completed - stats.value.partial)
    })

    const stateOptions = computed(() => {
      const complete = generations.value.filter(g => g.state === 'complete').length
      const partial = generations.value.filter(g => g.state === 'partial').length
      const failed = generations.value.filter(g => g.state === 'failed').length
      return [
        { value: 'all', label: 'All', count: generations.value.length },
        { value: 'complete', label: 'Complete', count: complete },
        { value: 'partial', label: 'Partial', count: partial },
        { value: 'failed', label: 'Failed', count: failed }
      ]
    })

    const filteredGenerations = computed(() => {
      const q = search.value.toLowerCase().trim()
      return generations.value.filter(g => {
        if (stateFilter.value !== 'all' && g.state !== stateFilter.value) return false
        if (q) {
          const hay = `${g.userId} ${g.reportId} ${g.language} ${g.matches.map(m => m.id).join(' ')}`.toLowerCase()
          if (!hay.includes(q)) return false
        }
        return true
      })
    })

    const filteredReports = computed(() => {
      const q = search.value.toLowerCase().trim()
      if (!q) return reports.value
      return reports.value.filter(r =>
        `${r.userId} ${r.reportId} ${r.title} ${r.user.name || ''} ${r.matches.map(m => m.name).join(' ')}`.toLowerCase().includes(q)
      )
    })

    // Join a report to its newest generation row (per-slice cost/timing lives there).
    const genForReport = (reportId) => reportId ? generations.value.find(g => g.reportId === reportId) || null : null

    const inFlightState = (r) => r.state === 'creating' || r.state === 'generating'
    const isRunning = (r) => inFlightState(r) && r.startedAtMs > 0 && (now.value - r.startedAtMs) <= STALE_MS
    const isStale = (r) => inFlightState(r) && (!r.startedAtMs || (now.value - r.startedAtMs) > STALE_MS)
    const runningCount = computed(() => reports.value.filter(isRunning).length)
    const staleCount = computed(() => reports.value.filter(isStale).length)
    const needsRepair = (r) => r.state === 'partial' || r.state === 'failed'

    // Runs currently generating (oldest-started first so the longest-running is on
    // top), for the live section. Derived from the onSnapshot feed + the ticker.
    const inFlight = computed(() =>
      reports.value.filter(inFlightState).slice().sort((a, b) => (a.startedAtMs || a.createdAtMs) - (b.startedAtMs || b.createdAtMs))
    )
    const readySections = (r) => TWINS_SLICES.filter(k => r.sections[k] === 'ready').length
    // The stage a live run is currently working on. The three matches run in
    // parallel, so every not-yet-ready match counts as active; synthesis only
    // after the matches land.
    const stageState = (r, sec) => {
      const v = r.sections[sec]
      if (v === 'ready') return 'ready'
      if (v === 'failed') return 'failed'
      if (!isRunning(r)) return 'pending'
      if (sec === 'synthesis') {
        const matchesDone = ['match1', 'match2', 'match3'].every(k => r.sections[k] === 'ready' || r.sections[k] === 'failed')
        return matchesDone ? 'active' : 'pending'
      }
      return 'active'
    }

    // Join matches metadata (photo/score/tier/drivers) with the per-section prose.
    const matchCards = (r) => r.matches.map(m => ({ ...m, prose: r.content['match' + m.slot] || null }))

    const patternKey = (rowId, slot, pi) => `${rowId}:${slot}:${pi}`
    const isPatternOpen = (rowId, slot, pi) => !!openPatterns.value[patternKey(rowId, slot, pi)]
    const togglePattern = (rowId, slot, pi) => {
      const k = patternKey(rowId, slot, pi)
      openPatterns.value[k] = !openPatterns.value[k]
    }

    const sliceAgg = computed(() => {
      const totals = {}
      const ms = {}
      const counts = {}
      TWINS_SLICES.forEach(k => { totals[k] = 0; ms[k] = 0; counts[k] = 0 })
      generations.value.forEach(g => {
        TWINS_SLICES.forEach(k => {
          const sl = g.slices && g.slices[k]
          if (sl) { totals[k] += num(sl.costUsd); ms[k] += num(sl.ms); counts[k] += 1 }
        })
      })
      const rows = TWINS_SLICES.map(k => ({ key: k, total: totals[k], avg: counts[k] ? totals[k] / counts[k] : 0, avgMs: counts[k] ? ms[k] / counts[k] : 0 }))
      const maxAvg = Math.max(...rows.map(r => r.avg), 0.0001)
      return rows.map(r => ({ ...r, pct: Math.round((r.avg / maxAvg) * 100) }))
    })

    const daySeries = computed(() => {
      const map = {}
      generations.value.forEach(g => {
        if (!g.createdAtMs) return
        const key = new Date(g.createdAtMs).toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' })
        map[key] = (map[key] || 0) + g.costUsd
      })
      return Object.keys(map).sort().map(date => ({ date, cost: map[date] }))
    })

    const destroyCharts = () => { [dayChart, sliceChart].forEach(c => c && c.destroy()); dayChart = sliceChart = null }

    const legendDark = { labels: { color: '#cfcfe1', padding: 12, font: { size: 12 }, usePointStyle: true } }
    const tooltipDark = { backgroundColor: '#1a1b2e', titleColor: '#fff', bodyColor: '#a9a9b3', borderColor: '#2c2d42', borderWidth: 1, padding: 12 }
    const baseScales = {
      x: { grid: { display: false }, ticks: { color: '#8b8b8b', font: { size: 11 }, maxRotation: 0, autoSkip: true } },
      y: { beginAtZero: true, grid: { color: '#2a2a2a' }, ticks: { color: '#8b8b8b', font: { size: 11 } } }
    }

    const renderCharts = () => {
      destroyCharts()
      if (!generations.value.length) return
      if (dayCanvas.value) {
        const series = daySeries.value
        dayChart = new Chart(dayCanvas.value.getContext('2d'), {
          type: 'bar',
          data: { labels: series.map(d => d.date), datasets: [{ label: 'Cost (USD)', data: series.map(d => +d.cost.toFixed(6)), backgroundColor: 'rgba(108,99,255,0.65)', borderColor: '#6c63ff', borderWidth: 1, borderRadius: 4 }] },
          options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { ...tooltipDark, callbacks: { label: c => ' $' + (c.parsed.y || 0).toFixed(4) } } }, scales: { ...baseScales, y: { ...baseScales.y, ticks: { ...baseScales.y.ticks, callback: v => '$' + v } } } }
        })
      }
      if (sliceCanvas.value) {
        const agg = sliceAgg.value.filter(s => s.total > 0)
        sliceChart = new Chart(sliceCanvas.value.getContext('2d'), {
          type: 'doughnut',
          data: { labels: agg.map(s => sliceLabel(s.key)), datasets: [{ data: agg.map(s => +s.total.toFixed(6)), backgroundColor: agg.map(s => sliceColor(s.key)), borderColor: '#1a1b2e', borderWidth: 2 }] },
          options: { responsive: true, maintainAspectRatio: false, cutout: '60%', plugins: { legend: { position: 'bottom', ...legendDark }, tooltip: { callbacks: { label: c => ` ${c.label}: $${(c.parsed || 0).toFixed(4)}` } } } }
        })
      }
    }

    const sliceOf = (row, key) => (row && row.slices && row.slices[key]) || {}
    const sectionState = (row, sec) => {
      const v = row.sections[sec]
      if (v === 'ready') return 'ready'
      if (v === 'failed') return 'failed'
      return 'pending'
    }
    const genStateVariant = (s) => ({ complete: 'success', partial: 'warning', failed: 'danger', generating: 'info', creating: 'info' }[s] || 'neutral')
    const reportStateVariant = (r) => {
      if (isStale(r)) return 'danger'
      if (isRunning(r)) return 'info'
      return { complete: 'success', partial: 'warning', failed: 'danger' }[r.state] || 'neutral'
    }
    const reportStateLabel = (r) => {
      if (isStale(r)) return 'stale'
      if (isRunning(r)) return 'running'
      return r.state || '—'
    }
    const tierVariant = (t) => ({ closest: 'success', strong: 'primary', match: 'neutral' }[t] || 'neutral')
    const reportDuration = (r) => {
      if (r.startedAtMs && r.finishedAtMs && r.finishedAtMs >= r.startedAtMs) return formatMs(r.finishedAtMs - r.startedAtMs)
      if (r.startedAtMs && (isRunning(r) || isStale(r))) return formatMs(now.value - r.startedAtMs) + ' …'
      return '—'
    }

    const sliceLabel = (k) => SLICE_LABELS[k] || (k ? k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—')
    const sliceColor = (k) => SLICE_COLORS[k] || '#8b8b9e'
    const modelShort = (m) => !m ? '—' : m.replace('claude-', '').replace(/-\d{8}$/, '')
    const truncate = (t, max) => !t ? '' : (t.length > max ? t.slice(0, max) + '…' : t)
    const formatCost = n => '$' + (n || 0).toFixed(n && n < 0.01 ? 6 : 4)
    const formatTokens = n => { const v = n || 0; if (v < 1000) return String(v); if (v < 1e6) return (v / 1e3).toFixed(1) + 'K'; return (v / 1e6).toFixed(2) + 'M' }
    const formatMs = ms => { const v = Math.round(ms || 0); if (v < 1000) return v + 'ms'; if (v < 60000) return (v / 1000).toFixed(1) + 's'; const m = Math.floor(v / 60000); const s = Math.round((v % 60000) / 1000); return s ? `${m}m ${s}s` : `${m}m` }
    const formatDate = ms => { if (!ms) return '—'; return new Date(ms).toLocaleString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) }
    const formatClock = ms => { if (!ms) return ''; return new Date(ms).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) }
    const openUser = (uid, event) => { if (event.metaKey || event.ctrlKey) window.open(`/user/${uid}`, '_blank'); else router.push(`/user/${uid}`) }
    const onImgError = (e) => { const el = e.target; el.style.display = 'none'; if (el.parentElement) el.parentElement.classList.add('img-failed') }

    watch([generations], () => renderCharts(), { flush: 'post' })

    onMounted(() => {
      load()
      startWatch()
      ticker = setInterval(() => { now.value = Date.now() }, 1000)
    })
    onBeforeUnmount(() => { if (ticker) clearInterval(ticker); if (unsub) unsub(); destroyCharts() })

    return {
      stats, generations, reports, errorLogs, loading, error, search, stateFilter, expandedGen, expandedReport, watching,
      genColumns, reportColumns, errorColumns, SLICES: TWINS_SLICES,
      avgCost, avgCostFull, avgMs, failedCount, stateOptions, filteredGenerations, filteredReports, sliceAgg,
      runningCount, staleCount, needsRepair, inFlight, readySections, stageState, isRunning, isStale, dayCanvas, sliceCanvas,
      genForReport, matchCards, isPatternOpen, togglePattern,
      load, sliceOf, num, sectionState, genStateVariant, reportStateVariant, reportStateLabel, tierVariant, reportDuration,
      sliceLabel, sliceColor, modelShort, truncate, formatCost, formatTokens, formatMs, formatDate, formatClock, openUser, onImgError
    }
  }
}
</script>

<style scoped>
.error-banner { background: rgba(248, 191, 113, 0.12); border: 1px solid rgba(248, 191, 113, 0.35); color: var(--warning); border-radius: 8px; padding: 10px 14px; font-size: 13px; }
.refresh-btn { padding: 9px 14px; background: var(--field-bg); border: 1px solid var(--panel-border); border-radius: var(--radius); color: var(--text-primary); font-size: 13px; cursor: pointer; }
.refresh-btn:hover { background: var(--hover-bg); }
.refresh-btn:disabled { opacity: 0.5; cursor: default; }
.link-btn { padding: 9px 14px; background: var(--field-bg); border: 1px solid var(--panel-border); border-radius: var(--radius); color: var(--primary-color); font-size: 13px; text-decoration: none; white-space: nowrap; }
.link-btn:hover { background: var(--hover-bg); }

.note { margin: 0; font-size: 12px; color: var(--text-secondary); }
.note code { font-size: 11px; background: var(--field-bg); padding: 1px 5px; border-radius: 4px; }

.chart-box { height: 300px; position: relative; }
.chart-box.small { height: 280px; }
.dual-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 16px; }

.slice-bars { display: flex; flex-direction: column; gap: 10px; }
.slice-bar-row { display: flex; align-items: center; gap: 10px; }
.sb-label { width: 84px; flex-shrink: 0; font-size: 12.5px; color: var(--text-primary); }
.sb-track { flex: 1; height: 8px; background: rgba(255, 255, 255, 0.06); border-radius: 4px; overflow: hidden; }
.sb-fill { height: 100%; border-radius: 4px; }
.sb-val { width: 74px; text-align: right; font-size: 12px; color: var(--text-secondary); font-variant-numeric: tabular-nums; }
.sb-val2 { width: 58px; text-align: right; font-size: 11.5px; color: var(--text-secondary); font-variant-numeric: tabular-nums; }

.filters { display: flex; gap: 12px; flex-wrap: wrap; }

.muted { color: var(--text-secondary); font-weight: 400; }
.small { font-size: 11px; }
.strong { font-weight: 600; }
.mono { font-family: monospace; font-size: 12px; }
.nowrap { white-space: nowrap; }
.date-cell { white-space: nowrap; }
.ellip { display: inline-block; max-width: 260px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; vertical-align: middle; }
.ellip.wide { max-width: 360px; }
.caret { display: inline-block; width: 11px; color: var(--text-secondary); transition: transform 0.18s ease; }
.caret.open { transform: rotate(90deg); color: var(--primary-color); }
.user-link { color: var(--primary-color); text-decoration: none; }
.user-link.small { font-size: 11px; }
.user-link:hover { text-decoration: underline; }
.repair-tag { margin-left: 6px; }
.failed { color: var(--danger); font-size: 12px; }
.running-flag { font-size: 12px; color: var(--info); font-weight: 600; }
.stale-flag { font-size: 12px; color: var(--danger); font-weight: 600; margin-left: 10px; }
.era-tag { font-size: 10px; text-transform: uppercase; letter-spacing: 0.4px; color: var(--text-secondary); }

/* matches mini-list (queue row) */
.match-mini { display: flex; flex-direction: column; gap: 3px; }
.mm-row { display: flex; align-items: center; gap: 6px; font-size: 12px; }
.mm-name { color: var(--text-primary); min-width: 96px; }
.mm-score { font-weight: 600; font-variant-numeric: tabular-nums; width: 24px; text-align: right; }

/* Live section */
.live-dot { display: inline-block; width: 9px; height: 9px; border-radius: 50%; background: var(--text-secondary); opacity: 0.5; margin-left: 8px; vertical-align: middle; }
.live-dot.on { background: var(--success); opacity: 1; box-shadow: 0 0 0 3px rgba(74, 222, 128, 0.2); animation: pulse 2s infinite; }
@keyframes pulse { 50% { opacity: 0.5; } }
.live-idle { padding: 16px 4px; color: var(--text-secondary); font-size: 13px; }
.live-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 14px; }
.live-card { border: 1px solid var(--panel-border); border-radius: var(--radius); background: var(--field-bg); padding: 14px 16px; }
.live-card.stale { border-color: rgba(255, 107, 107, 0.4); background: rgba(255, 107, 107, 0.06); }
.lc-top { display: flex; align-items: center; gap: 8px; }
.lc-title { font-weight: 600; font-size: 14px; color: var(--text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.lc-elapsed { margin-left: auto; font-size: 12px; font-variant-numeric: tabular-nums; color: var(--text-secondary); white-space: nowrap; }
.lc-meta { margin-top: 6px; font-size: 12px; color: var(--text-secondary); }
.lc-meta b { color: var(--text-primary); }
.lc-stages { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 12px; }
.stage { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; padding: 3px 9px; border-radius: 12px; border: 1px solid var(--panel-border); background: var(--panel-bg); color: var(--text-secondary); transition: all 0.25s ease; }
.stage .stage-ic { font-size: 10px; font-weight: 700; }
.stage.st-ready { background: rgba(74, 222, 128, 0.14); color: var(--success); border-color: rgba(74, 222, 128, 0.32); }
.stage.st-failed { background: rgba(255, 107, 107, 0.16); color: var(--danger); border-color: rgba(255, 107, 107, 0.38); }
.stage.st-active { background: rgba(108, 99, 255, 0.18); color: var(--primary-color); border-color: rgba(108, 99, 255, 0.45); animation: pulse 1.3s infinite; }
.lc-prog { height: 6px; background: rgba(255, 255, 255, 0.08); border-radius: 4px; overflow: hidden; margin-top: 12px; }
.lc-prog-fill { height: 100%; background: linear-gradient(90deg, var(--primary-color), var(--gradient-2)); border-radius: 4px; transition: width 0.4s ease; }
.lc-count { margin-top: 6px; font-size: 11px; color: var(--text-secondary); }

/* section progress dots */
.dots { display: inline-flex; gap: 4px; }
.dot { width: 9px; height: 9px; border-radius: 50%; background: rgba(255,255,255,0.14); display: inline-block; }
.dot.d-ready { background: var(--success); }
.dot.d-failed { background: var(--danger); }

/* report detail */
.rep-detail { padding: 14px 16px 20px; }
.rd-section { margin-bottom: 16px; }
.rd-title { font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-secondary); margin-bottom: 8px; }
.reader-three { display: flex; flex-wrap: wrap; gap: 8px; }
.three-pill { display: inline-flex; align-items: center; gap: 6px; font-size: 12.5px; padding: 4px 10px; border-radius: 12px; background: var(--field-bg); border: 1px solid var(--panel-border); }
.three-pill span { color: var(--text-secondary); font-size: 11px; }
.three-pill b { color: var(--text-primary); font-weight: 500; }

.match-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 14px; margin-bottom: 16px; }
.match-card { border: 1px solid var(--panel-border); border-radius: var(--radius); background: var(--field-bg); padding: 14px; }
.mc-head { display: flex; gap: 12px; align-items: flex-start; }
.mc-photo { width: 56px; height: 56px; flex-shrink: 0; border-radius: 10px; overflow: hidden; background: var(--panel-bg); border: 1px solid var(--panel-border); }
.mc-photo img { width: 100%; height: 100%; object-fit: cover; }
.mc-nophoto { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; font-size: 22px; font-weight: 700; color: var(--text-secondary); }
.mc-id { flex: 1; min-width: 0; }
.mc-name { font-weight: 600; font-size: 14px; color: var(--text-primary); }
.mc-sub { font-size: 11.5px; color: var(--text-secondary); margin-top: 2px; }
.mc-three { font-size: 11.5px; color: var(--text-primary); margin-top: 4px; letter-spacing: 0.3px; }
.mc-score { text-align: center; flex-shrink: 0; }
.mc-score-num { font-size: 22px; font-weight: 700; color: var(--text-primary); line-height: 1; margin-bottom: 4px; }
.mc-drivers { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 10px; }
.driver-chip { font-size: 11px; padding: 2px 8px; border-radius: 10px; background: rgba(108,99,255,0.12); color: var(--primary-color); border: 1px solid rgba(108,99,255,0.25); }
.mc-profession { font-size: 12px; color: var(--text-secondary); margin-top: 10px; font-style: italic; }
.mc-traits { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 8px; }
.trait-chip { font-size: 11px; padding: 2px 8px; border-radius: 10px; background: var(--panel-bg); border: 1px solid var(--panel-border); color: var(--text-primary); }
.mc-summary { font-size: 13px; line-height: 1.55; color: var(--text-primary); margin: 10px 0 0; }

.patterns { margin-top: 10px; display: flex; flex-direction: column; gap: 6px; }
.pattern { border: 1px solid var(--panel-border); border-radius: 8px; background: var(--panel-bg); padding: 8px 10px; cursor: pointer; }
.pattern.open { border-color: rgba(108,99,255,0.4); }
.pat-head { display: flex; align-items: baseline; gap: 6px; }
.pat-title { font-weight: 600; font-size: 12.5px; color: var(--text-primary); }
.pat-hook { font-size: 12px; color: var(--text-secondary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pattern.open .pat-hook { white-space: normal; }
.pat-body { margin-top: 8px; padding-left: 17px; }
.pat-body p { font-size: 12.5px; line-height: 1.5; margin: 0 0 6px; color: var(--text-primary); }
.pat-who { font-size: 10px; text-transform: uppercase; letter-spacing: 0.4px; color: var(--primary-color); margin-right: 5px; font-weight: 700; }
.pat-shared { font-size: 11px; color: var(--text-secondary); font-family: monospace; }

.mc-mini-title { font-size: 12px; font-weight: 600; color: var(--text-primary); margin: 12px 0 6px; }
.mc-note { font-size: 12.5px; line-height: 1.5; color: var(--text-secondary); margin: 0; }
.mc-houses { margin-top: 6px; }

.paths { margin-top: 6px; }
.paths-table { width: 100%; border-collapse: separate; border-spacing: 0; margin-bottom: 6px; }
.paths-table th, .paths-table td { text-align: left; padding: 5px 8px; font-size: 12px; border-bottom: 1px solid var(--border-soft); }
.paths-table th { font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.4px; color: var(--text-secondary); }
.paths-table td { color: var(--text-primary); }
.pt-who { font-weight: 600; color: var(--text-secondary); }

.learn { margin-top: 6px; }
.try-list { margin: 6px 0 0; padding-left: 18px; }
.try-list li { font-size: 12.5px; line-height: 1.5; color: var(--text-primary); margin-bottom: 3px; }
.share-trait { margin-top: 10px; font-size: 12px; color: var(--text-secondary); }
.share-trait b { color: var(--text-primary); }
.mc-pending { margin-top: 12px; font-size: 12.5px; color: var(--text-secondary); }

.synthesis { border-top: 1px solid var(--border-soft); padding-top: 14px; }
.syn-headline { font-size: 15px; font-weight: 600; color: var(--text-primary); margin-bottom: 6px; }

.parts-grid { display: flex; flex-wrap: wrap; gap: 6px; }
.part-chip { display: inline-flex; align-items: center; gap: 5px; font-size: 11.5px; padding: 3px 9px; border-radius: 12px; border: 1px solid var(--panel-border); background: var(--field-bg); color: var(--text-secondary); }
.part-chip.ps-ready { background: rgba(74, 222, 128, 0.14); color: var(--success); border-color: rgba(74, 222, 128, 0.32); }
.part-chip.ps-failed { background: rgba(255, 107, 107, 0.16); color: var(--danger); border-color: rgba(255, 107, 107, 0.38); }
.part-at { font-size: 9.5px; opacity: 0.7; }
.rd-run-meta { display: flex; gap: 16px; flex-wrap: wrap; margin-top: 10px; font-size: 12px; color: var(--text-secondary); }
.rd-run-meta code { background: var(--field-bg); padding: 1px 6px; border-radius: 4px; font-size: 11px; }

/* expanded per-slice detail (generations) */
.slice-detail { padding: 14px 16px 16px; }
.slice-table { width: 100%; border-collapse: separate; border-spacing: 0; }
.slice-table th, .slice-table td { text-align: left; padding: 7px 10px; border-bottom: 1px solid var(--border-soft); font-size: 12.5px; }
.slice-table th { font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-secondary); font-weight: 600; }
.slice-table td { color: var(--text-primary); }
.slice-table tr:last-child td { border-bottom: none; }
.sl-name { font-weight: 600; }
.sl-model { color: var(--text-secondary); font-family: monospace; font-size: 11.5px; }
.slice-missing td { opacity: 0.45; }
.ok-yes { color: var(--success); font-weight: 700; }
.ok-no { color: var(--danger); font-weight: 700; }
.slice-foot { display: flex; gap: 18px; flex-wrap: wrap; margin-top: 10px; font-size: 12px; color: var(--text-secondary); }
.slice-foot code { background: var(--field-bg); padding: 1px 6px; border-radius: 4px; font-size: 11px; }
</style>
